import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";

import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext/AuthContext";
import CustomBottomNavbar from "../../Users/Post Module/FormsBottomNavbar";

const formatPropertyId = (id) => {
  return `BAP${String(id).padStart(4, '0')}`;
};

const getImageUrl = (docArray) => {
  if (!docArray || docArray.length === 0) {
    return null; // Return null if no images
  }
  
  const docUrl = docArray[0]?.document;
  
  if (docUrl) {
    if (docUrl.startsWith('/')) {
      return `https://landnest.net:81${docUrl}`;
    }
    return docUrl;
  }
  
  return null; 
};

const User_BankAuction_detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const imageUrl = data ? getImageUrl(data.bank_pro_doc) : null;

  const handleCallButtonClick = () => {
    if (data?.bank_contact_details) {
      navigator.clipboard.writeText(data.bank_contact_details)
        .then(() => {
          window.alert(`Contact number copied: ${data.bank_contact_details}\n\nProperty ID: ${formatPropertyId(data.bankprop_id)}`);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          window.alert("Failed to copy contact number");
        });
    } else {
      window.alert("Contact number not available");
    }
  };

  const handleLocationClick = () => {
    setLocationLoading(true);
    
    const openInMaps = (url) => {
      setLocationLoading(false);
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    const locationString =  `${data.lat},${data.long}`;
    const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationString)}`;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          const mapsUrl = data.lat && data.long
            ? `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${data.lat},${data.long}&travelmode=driving`
            : fallbackUrl;
          openInMaps(mapsUrl);
        },
        (error) => {
          console.error("Error getting location:", error);
          openInMaps(fallbackUrl);
        },
        { timeout: 10000 }
      );
    } else {
      openInMaps(fallbackUrl);
    }
  };

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        if (!userId) {
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `https://landnest.net:81/auction-property/?user_id=${userId}`
        );

        const selected = res.data.find(
          (item) => String(item.bankprop_id) === String(id)
        );

        setData(selected || null);
        setLoading(false);
        setImageError(false); // Reset image error when new data loads
      } catch (err) {
        console.error("Auction detail error:", err);
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id, userId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography>No auction details found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 9 }}> 
      <Card sx={{ maxWidth: 420, mx: "auto", boxShadow: 4 }}>
        {/* ================= HEADER ================= */}
        <Box
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton sx={{ color: "#fff" }} onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          <Typography fontWeight={600} flexGrow={1}>
            Auction Property
          </Typography>

          <Box textAlign="right">
            <Typography variant="caption">
              ID: {formatPropertyId(data.bankprop_id)}
            </Typography>
            <Typography variant="caption" display="block">
              {new Date(data.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        {/* ================= BODY ================= */}
        <Box sx={{ p: 2 }}>
          {/* IMAGE - DYNAMIC */}
          <Box position="relative">
            {imageUrl && !imageError ? (
              <CardMedia
                component="img"
                height="190"
                image={imageUrl}
                alt="Property"
                onError={() => setImageError(true)} // If image fails to load
              />
            ) : (
              <Box
                sx={{
                  height: 190,
                  backgroundColor: "#e6e0d4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No Image
                </Typography>
              </Box>
            )}
          </Box>

          <CardContent>
            {/* PRICE */}
            <Box
              sx={{
                backgroundColor: "#fff1f0",
                p: 2,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2">Reserve Price</Typography>
              <Typography variant="h6" color="error" fontWeight={700}>
                ₹{Number(data.reserve_price).toLocaleString()}
              </Typography>
            </Box>

            <Typography variant="body2">
              <b>Property:</b> {data.property_type}
            </Typography>

            <Typography variant="body2" mt={1}>
              <b>City/Town:</b> {data.city_town}
            </Typography>

            <Box mt={1}>
              <Box
                onClick={handleLocationClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                {/* Location Text */}
                <Typography variant="body2">
                  <b>Location:</b>
                </Typography>

                {/* Icon / Loader */}
                {locationLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <LocationOnIcon
                    sx={{ color: "#1976d2", fontSize: "1rem" }}
                  />
                )}
              </Box>
            </Box>

            <Typography variant="body2" mt={1}>
              <b>Area:</b> {data.area} {data.units}
            </Typography>

            {/* DETAILS */}
            <Box
              sx={{
                backgroundColor: "#f9f9f9",
                p: 2,
                borderRadius: 2,
                mt: 2,
              }}
            >
              <InfoRow
                label="EMD Amount"
                value={`₹${Number(data.emd_amount).toLocaleString()}`}
              />
              <InfoRow
                label="Bid Increment"
                value={`₹${Number(data.bid_increment).toLocaleString()}`}
              />
              <InfoRow
                label="Auction Start"
                value={new Date(data.auction_start_datetime).toLocaleString()}
              />
              <InfoRow
                label="Auction End"
                value={new Date(data.auction_end_datetime).toLocaleString()}
              />
              <InfoRow label="Possession" value={data.possession_status} />
            </Box>

            <Button fullWidth variant="contained" sx={{ mt: 2 }}>
              Download Auction File
            </Button>

            <Divider sx={{ my: 2 }} />

            {/* BANK */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography fontWeight={600}>{data.bank_name}</Typography>
                <Typography variant="body2">
                  Contact: {data.bank_contact_details}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<CallIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleCallButtonClick} 
              >
                Call
              </Button>
            </Box>
          </CardContent>
        </Box>
      </Card>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
        }}
      >
        <CustomBottomNavbar />
      </Box>
    </Box>
  );
};

/* ============ INFO ROW COMPONENT ============ */
const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

export default User_BankAuction_detail;